import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHourglass, faLocationDot } from "@fortawesome/pro-light-svg-icons"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { CARD_HEIGHT, CARD_WIDTH } from "../dashboardCardDimensions"

import {
    type LunchSubscription,
    type SubscriptionKitchen,
    type SubscriptionUsageDetails,
    SubscriptionUsagePeriod,
} from "./subscriptionsTypes"

type StyleProps = Readonly<{
    isActive: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { isActive }) => ({
    card: {
        flex: "0 0 auto",
        width: CARD_WIDTH,
        minHeight: CARD_HEIGHT,
        borderRadius: ".75rem",
        background: isActive ? theme.palette.tailwind.blue[100] : theme.palette.tailwind.amber[50],
        display: "flex",
        flexDirection: "column",
    },
    topSection: {
        flex: 1,
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: ".375rem",
        borderBottom: `1px dashed ${theme.palette.divider}`,
    },
    bottomSection: {
        padding: ".5rem 1rem",
        display: "flex",
        flexDirection: "column",
    },
    title: {
        display: "block",
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    usageRow: {
        display: "flex",
        alignItems: "center",
    },
    usageBadge: {
        background: theme.palette.common.black,
        color: theme.palette.common.white,
        borderRadius: ".25rem",
        padding: ".125rem .25rem",
        marginRight: ".375rem",
        fontSize: ".75rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
    usageLabel: {
        fontSize: ".75rem",
        color: theme.palette.text.secondary,
        fontWeight: 400,
    },
    infoRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: ".375rem",
        fontSize: ".75rem",
        fontWeight: 400,
        color: theme.palette.text.secondary,
        "&:not(:last-child)": {
            marginBottom: ".375rem",
        },
    },
    infoIcon: {
        fontSize: ".75rem",
        marginTop: ".1875rem",
        color: "currentColor",
    },
}))

type SubscriptionCardProps = Readonly<{
    lunchSubscription: LunchSubscription
    kitchen: SubscriptionKitchen
    usageDetails?: SubscriptionUsageDetails
    subscriptionDate?: string
    unsubscribeDate?: string
}>

export function SubscriptionCard({
    lunchSubscription,
    kitchen,
    usageDetails,
    subscriptionDate,
    unsubscribeDate,
}: SubscriptionCardProps) {
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now().startOf("day")
    const subDate = subscriptionDate ? dateTimeFactory.fromApi(subscriptionDate).startOf("day") : null
    const unsubDate = unsubscribeDate ? dateTimeFactory.fromApi(unsubscribeDate).startOf("day") : null

    const isPendingSubscription = !!subDate && subDate > today
    const isPendingUnsubscription = !!unsubDate && unsubDate > today
    const isActive = !isPendingSubscription

    const { classes } = useStyles({ isActive })

    const displayDate = isPendingSubscription ? subDate : isPendingUnsubscription ? unsubDate : null
    const formattedDate = displayDate ? dateTimeFactory.formatDate(displayDate) : null
    const periodMessageId = usageDetails ? getUsagePeriodMessageId(usageDetails.usagePeriod) : null

    return (
        <Box className={classes.card}>
            <Box className={classes.topSection}>
                <Typography component="span" variant="body1" className={classes.title}>
                    {lunchSubscription.subscriptionName}
                </Typography>
                {isActive && usageDetails && (
                    <Box className={classes.usageRow}>
                        <LocalizedStrict
                            id="user-home-subscription-card-used-count"
                            vars={{ used: usageDetails.used, total: usageDetails.limit }}
                        >
                            <Typography component="span" className={classes.usageBadge}>
                                {usageDetails.used} of {usageDetails.limit}
                            </Typography>
                        </LocalizedStrict>
                        {periodMessageId && (
                            <LocalizedStrict id={periodMessageId}>
                                <Typography component="span" className={classes.usageLabel}>
                                    used this period
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </Box>
                )}
                {formattedDate && (
                    <Box className={classes.infoRow}>
                        <FontAwesomeIcon icon={faHourglass} className={classes.infoIcon} />
                        {isPendingSubscription ? (
                            <LocalizedStrict
                                id="user-home-subscription-card-active-from"
                                vars={{ date: formattedDate }}
                            >
                                <Typography component="span" variant="inherit">
                                    Active from: {formattedDate}
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="user-home-subscription-card-expires" vars={{ date: formattedDate }}>
                                <Typography component="span" variant="inherit">
                                    Expires: {formattedDate}
                                </Typography>
                            </LocalizedStrict>
                        )}
                    </Box>
                )}
            </Box>

            <Box className={classes.bottomSection}>
                <Box className={classes.infoRow}>
                    <FontAwesomeIcon icon={faLocationDot} className={classes.infoIcon} />
                    <Typography component="span" variant="inherit">
                        {kitchen.name}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

function getUsagePeriodMessageId(period: string | undefined) {
    if (period === SubscriptionUsagePeriod.WEEK) return "user-home-subscription-card-used-this-week"
    if (period === SubscriptionUsagePeriod.MONTH) return "user-home-subscription-card-used-this-month"
    return null
}
